import {
  getDashboardLeagueCustomTagsControllerListQueryKey,
  getDashboardLeagueLevelsControllerListQueryKey,
  getDashboardLeagueRegionsControllerListQueryKey,
  getDashboardLeagueSeasonsControllerListQueryKey,
} from "@/api/__generated__/dashboard-league-setup/dashboard-league-setup";
import { MessageKey } from "@/i18n";
import { LeagueSetupResource } from "@/types";

/**
 * Query-key prefixes for the four League setup list endpoints, derived from
 * the generated query-key helpers (`[url, params]`) rather than hardcoded
 * strings — a renamed endpoint surfaces as a type error here. The generated
 * helpers require `params` (leagueId/keyword), so we can't precompute a single
 * concrete key; React Query matches by prefix, so invalidating just the URL
 * segment invalidates every variant of that list.
 */
const LEAGUE_SEASONS_LIST_QUERY_KEY_PREFIX = [
  getDashboardLeagueSeasonsControllerListQueryKey({ leagueId: "" })[0],
] as const;
const LEAGUE_REGIONS_LIST_QUERY_KEY_PREFIX = [
  getDashboardLeagueRegionsControllerListQueryKey({ leagueId: "" })[0],
] as const;
const LEAGUE_LEVELS_LIST_QUERY_KEY_PREFIX = [
  getDashboardLeagueLevelsControllerListQueryKey({ leagueId: "" })[0],
] as const;
const LEAGUE_CUSTOM_TAGS_LIST_QUERY_KEY_PREFIX = [
  getDashboardLeagueCustomTagsControllerListQueryKey({ leagueId: "" })[0],
] as const;

/**
 * The four lists feed each other's multiselects (a season is an option in the
 * region/level/tag modals, a region in the level/tag modals, …). So after any
 * create / update / delete of a season, region, or level we invalidate ALL four
 * list keys, not just the mutated resource's own list. Reused from every panel
 * and modal that mutates a league-setup resource.
 */
export const LEAGUE_SETUP_INVALIDATE_KEYS = [
  LEAGUE_SEASONS_LIST_QUERY_KEY_PREFIX,
  LEAGUE_REGIONS_LIST_QUERY_KEY_PREFIX,
  LEAGUE_LEVELS_LIST_QUERY_KEY_PREFIX,
  LEAGUE_CUSTOM_TAGS_LIST_QUERY_KEY_PREFIX,
];

/** Segmented sub-tab labels for the League setup tab. */
export const LEAGUE_SETUP_RESOURCE_LABELS: { [key in LeagueSetupResource]: MessageKey } = {
  seasons: "leagueSetup.tabs.seasons",
  regions: "leagueSetup.tabs.regions",
  levels: "leagueSetup.tabs.levels",
  customTags: "leagueSetup.tabs.customTags",
};
